import type { Claim } from "@opulancia/renderer/editorial/assemble";
import type { EditorialOutline } from "@opulancia/renderer/editorial/authored-core";
import type { StudioDoc } from "../studio/model.js";
import { coverCheckIds, fingerprint, type ClaimReview, type Preparation } from "./model.js";
import type { Envelope } from "./scenario.js";

/**
 * Where a preparation stands, without the checks (scenario.ts, loaded with the preparation
 * screen only): claim states, fingerprints of what was reviewed, the editorial status of a
 * built project. Read by the editor of every prepared project.
 */
export type ClaimStateId = "confirmed" | "rejected" | "to_verify" | "review_outdated";
export const CLAIM_STATE_LABEL: Record<ClaimStateId, string> = {
  confirmed: "Confirmé par un membre",
  rejected: "Rejeté par un membre",
  to_verify: "À vérifier",
  review_outdated: "À vérifier (le texte a changé depuis la lecture)",
};

export interface ClaimState {
  claim: Claim;
  /** As the conversation wrote it: never a validation. */
  conversation: Claim["status"];
  state: ClaimStateId;
  review: ClaimReview | null;
  /** Slides (1-based) that cite it. */
  slides: number[];
}

export const claimHash = (claim: Pick<Claim, "text" | "source_urls">) => fingerprint({ text: claim.text, sources: claim.source_urls });

/** The latest reading of a claim by a member, and whether it still applies to its text. */
export function claimReview(prep: Preparation, claim: Claim): { review: ClaimReview | null; current: boolean } {
  const review = [...prep.claim_reviews].reverse().find((r) => r.claim_id === claim.id) ?? null;
  return { review, current: !!review && review.claim_hash === claimHash(claim) };
}

/** Every claim of the answer with its state (the conversation's status is never a validation). */
export function claimStates(prep: Preparation, outline?: EditorialOutline): ClaimState[] {
  const envelope = prep.envelope as unknown as Envelope | null;
  if (!envelope?.request?.research) return [];
  const citing = new Map<string, number[]>();
  (outline ?? envelope.outline).slides.forEach((s, i) => s.claim_ids.forEach((id) => citing.set(id, [...(citing.get(id) ?? []), i + 1])));
  return envelope.request.research.claims.map((claim) => {
    const { review, current } = claimReview(prep, claim);
    const state: ClaimStateId = current ? (review!.decision === "confirmed" ? "confirmed" : "rejected") : review ? "review_outdated" : "to_verify";
    return { claim, conversation: claim.status, state, review, slides: citing.get(claim.id) ?? [] };
  });
}

/** What a member's excerpt has in common with the claim, for information only. */
export function excerptHints(claimText: string, excerpt: string): { numbers: Array<{ value: string; found: boolean }>; note: string } {
  const numbers = [...new Set(claimText.replace(/(\d)[\s  ](?=\d{3}\b)/g, "$1").match(/\d+(?:[.,]\d+)?/g) ?? [])];
  const inExcerpt = new Set(excerpt.replace(/(\d)[\s  ](?=\d{3}\b)/g, "$1").match(/\d+(?:[.,]\d+)?/g) ?? []);
  return {
    numbers: numbers.map((value) => ({ value, found: inExcerpt.has(value) })),
    note: "Indication seulement : retrouver un nombre ou un mot dans le passage ne prouve pas que l'affirmation le reformule correctement. C'est la lecture du membre qui confirme.",
  };
}

/** Texts of the slides (what an editorial review covers). */
export const textsFingerprint = (doc: StudioDoc) => fingerprint(doc.slides.map((s) => [s.kind, s.title, s.sub, s.accent, s.highlight, s.objective ?? null, s.objective === "save" ? (s.button ?? "") : null]));

/** What the cover check covers: the cover's text, image and framing. */
export function coverFingerprint(doc: StudioDoc): string {
  const c = doc.slides.find((s) => s.kind === "cover");
  // fadeStrength only when it differs from the generator's fade: checks made before it existed stay valid
  return fingerprint(c ? [c.title, c.accent, c.highlight, c.image?.sha256 ?? null, c.fx, c.fy, c.zoom, c.darken, c.fade, c.align, c.position, c.titleSize, ...(c.fadeStrength !== 1 ? [c.fadeStrength] : [])] : null);
}

export interface PreparedStatus {
  /** Claims cited by the project's slides: how many a member confirmed, which remain open or are rejected. */
  facts: { shown: number; confirmed: number; open: string[]; rejected: string[] };
  /** The editorial review: none yet, still valid, or to renew (a text changed since). */
  review: "none" | "valid" | "outdated";
  cover: "none" | "valid" | "partial" | "outdated";
  /** Slides (1-based) that have a brief and no image yet. */
  missingImages: number[];
  /** Why the editorial review cannot be recorded yet (null: it can). */
  reviewBlocked: string | null;
}

/** Where a built project stands, editorially (the PNG export is a separate, technical matter). */
export function preparedStatus(doc: StudioDoc): PreparedStatus | null {
  const prep = doc.preparation;
  if (!prep || prep.stage !== "built") return null;
  const links = new Map(prep.slide_links.map((l) => [l.slide_id, l]));
  const cited = new Set(doc.slides.flatMap((s) => links.get(s.id)?.claim_ids ?? []));
  const states = claimStates(prep).filter((c) => cited.has(c.claim.id));
  const confirmed = states.filter((c) => c.state === "confirmed").length;
  const open = states.filter((c) => c.state === "to_verify" || c.state === "review_outdated").map((c) => c.claim.id);
  const rejected = states.filter((c) => c.state === "rejected").map((c) => c.claim.id);
  const missingImages = doc.slides.flatMap((s, i) => (links.get(s.id)?.brief && !s.image ? [i + 1] : []));
  const review = !prep.review ? "none" : prep.review.fingerprint === textsFingerprint(doc) ? "valid" : "outdated";
  const check = prep.cover_check;
  const cover = !check ? "none" : check.fingerprint !== coverFingerprint(doc) ? "outdated" : coverCheckIds(check.version).every((id) => check.items[id]) ? "valid" : "partial";
  const reviewBlocked = rejected.length ? `fait(s) rejeté(s) encore affiché(s) : ${rejected.join(", ")}` : open.length ? `fait(s) à vérifier : ${open.join(", ")}` : null;
  return { facts: { shown: states.length, confirmed, open, rejected }, review, cover, missingImages, reviewBlocked };
}

/** Where the cover comparison stands (the editor shows it; the comparator itself is in cover.ts). */
export function studyState(prep: Preparation): "none" | "started" | "decided" {
  const study = prep.cover_study;
  if (!study) return "none";
  if (study.decision) return "decided";
  return study.concepts.some((c) => c.image || Object.keys(c.answers).length || c.glance) ? "started" : "none";
}
